// Product analytics (PostHog, US cloud). Only a build with VITE_POSTHOG_KEY sends anything, so
// local, test and smoke builds stay silent. Events go through the game's own /ingest path
// (deploy/nginx.player.conf), which ad blockers leave alone and which forwards no cookies.
// The PostHog cookie is shared across *.meomeo.io, so the Rails player pages on the API host
// (mew-backend app/assets/javascripts/analytics.js) continue the same visitor.
import { apiUrl, apiCredentials } from '../api-origin.js'

const env = import.meta.env || {}

export function analyticsConfig(values = env) {
  const key = String(values.VITE_POSTHOG_KEY || '').trim()
  if (!/^phc_[A-Za-z0-9]+$/.test(key)) return null
  return { key, host: String(values.VITE_POSTHOG_HOST || '').trim() || '/ingest' }
}

// Who the session endpoint says is viewing. Members are identified by their player id (the
// same distinct id the Rails pages use) with their public handle; guests and visitors stay
// anonymous and only carry their kind.
export function viewerIdentity(session) {
  const user = session?.user
  if (!user) return { kind: 'visitor' }
  if (user.guest) return { kind: 'guest' }
  return { kind: 'member', id: String(user.id), handle: session.page?.handle || null }
}

export function applyViewer(posthog, viewer) {
  if (viewer.id) posthog.identify(viewer.id, viewer.handle ? { handle: viewer.handle } : undefined)
  // Signed out (or a guest now) on a browser that last saw a member: start a new anonymous visitor.
  else if (posthog.get_property('$user_state') === 'identified') posthog.reset()
  posthog.register({ player_kind: viewer.kind })
}

const loadedPostHog = () => (typeof window === 'undefined' ? null : window.posthog?.capture ? window.posthog : null)

// Event properties are ids, kinds, counts and flags. Any other string (chat text, an email, a
// title) is dropped here, so a careless call site still cannot send free text.
export function eventProperties(properties = {}) {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value === null || typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value)) || (typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(value))))
}

// A named product event: guest_started, signed_up, signed_in (play entry, account panel),
// story_play_started, chapter_completed, player_message_sent (play), studio_draft_created,
// story_published, copilot_message_sent (Story Studio). A no-op when PostHog is not loaded:
// tests, local and smoke builds without VITE_POSTHOG_KEY. Never throws.
export function track(name, properties) {
  const posthog = loadedPostHog()
  if (!posthog) return false
  try { posthog.capture(name, eventProperties(properties)) } catch { return false }
  return true
}

// After signing in or up on a page that stays open, what follows belongs to the new member.
export function identifySession(session) {
  const posthog = loadedPostHog()
  if (posthog) try { applyViewer(posthog, viewerIdentity(session)) } catch {}
}

// Entering as a guest, signing up or signing in (the player client's guest/signup/login).
export function trackAuth(mode, session, properties) {
  const name = { guest: 'guest_started', signup: 'signed_up', login: 'signed_in' }[mode]
  if (!name) return false
  identifySession(session)
  return track(name, properties)
}

async function identifyViewer(posthog) {
  const url = apiUrl('/api/player/session')
  try {
    const response = await fetch(url, { credentials: apiCredentials(url), cache: 'no-store', headers: { Accept: 'application/json' } })
    if (response.ok) applyViewer(posthog, viewerIdentity(await response.json()))
  } catch {}
}

// PostHog's official loader snippet: a stub queues calls until /static/array.js arrives.
function loadSnippet() {
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  return window.posthog
}

// Console warnings and errors become PostHog logs. Info and debug lines stay in the browser:
// they are many, and some repeat story or chat text.
export function keepWarningsAndErrors(record) {
  return ['warn', 'error', 'fatal'].includes(record?.level) ? record : null
}

export function analyticsOptions(config) {
  return {
    api_host: config.host,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    // Error tracking: uncaught errors and unhandled promise rejections. Readable stack traces
    // need the build's source maps uploaded (Dockerfile, POSTHOG_CLI_API_KEY build secret).
    capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
    logs: { captureConsoleLogs: true, beforeSend: keepWarningsAndErrors },
  }
}

// `identify: false` is for a page that signs in as it loads (quest.html): it names the viewer
// itself with identifySession. The session read here could answer after that sign-in, and the
// API's answer to a read sent before it still sets the signed-out cookie.
export function startAnalytics(config = analyticsConfig(), { identify = true } = {}) {
  if (!config || typeof window === 'undefined' || window.posthog?.__loaded) return null
  const posthog = loadSnippet()
  posthog.init(config.key, { ...analyticsOptions(config), ...(identify ? { loaded: identifyViewer } : {}) })
  return posthog
}
